if (!customElements.get('customer-reviews-component')) {
  class CustomerReviews extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.section = this.closest('.customer-reviews');
      this.sliderWrapper = this.querySelector('.customer-reviews__items');
      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items, 10) || 3;
      this.tabletItems = parseInt(this.dataset.tabletItems, 10) || 2;
      this.sectionId = this.dataset.sectionId;
      this.modalId = this.dataset.modalId;
      this.sliderInstance = false;

      this.setupReadMoreModal();

      if (this.enableSlider && this.sliderWrapper) {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          this.init();
        }
        document.addEventListener('matchMobile', () => this.init());
        document.addEventListener('unmatchMobile', () => this.init());
      }
    }

    setupReadMoreModal() {
      const debounce = (fn, wait = 150) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      };

      const updateClamp = () => {
        this.querySelectorAll('.customer-review-card').forEach((card) => {
          const clampEl = card.querySelector('[data-review-clamp]');
          const moreBtn = card.querySelector('[data-review-more]');
          const fullEl = card.querySelector('[data-review-full]');
          if (!clampEl || !moreBtn || !fullEl) return;

          // If no modal id, we can't open a modal.
          const controls = moreBtn.getAttribute('aria-controls');
          if (!controls) {
            moreBtn.hidden = true;
            return;
          }

          // Prevent swiper from treating this as a swipe (but allow click to bubble).
          if (!moreBtn.dataset.bound) {
            const stop = (ev) => {
              // Only stop propagation for pointer/touch/mouse down.
              if (ev.type === 'pointerdown' || ev.type === 'touchstart' || ev.type === 'mousedown') {
                ev.stopPropagation();
              }
            };
            moreBtn.addEventListener('pointerdown', stop);
            moreBtn.addEventListener('touchstart', stop, { passive: true });
            moreBtn.addEventListener('mousedown', stop);
            moreBtn.dataset.bound = 'true';
          }

          // FIRST: reset any previous clamp so we can measure/full check correctly.
          clampEl.classList.remove('is-clamped');
          moreBtn.hidden = true;

          // Measure based on actual rendered lines:
          // - If text <= 5 lines: show all text, no clamp, no button.
          // - If text > 5 lines: clamp to 5 lines (CSS) and show button.
          const style = window.getComputedStyle(clampEl);
          let lineHeight = parseFloat(style.lineHeight);
          if (!lineHeight || Number.isNaN(lineHeight)) {
            const fontSize = parseFloat(style.fontSize) || 16;
            lineHeight = fontSize * 1.5;
          }
          const paddingTop = parseFloat(style.paddingTop) || 0;
          const paddingBottom = parseFloat(style.paddingBottom) || 0;
          const contentHeight = clampEl.scrollHeight - paddingTop - paddingBottom;
          const totalLines = contentHeight / lineHeight;

          if (totalLines > 5.05) {
            clampEl.classList.add('is-clamped');
            moreBtn.hidden = false;
          }
        });

        // After clamp/visibility changes have been applied, equalize body height.
        // requestAnimationFrame(() => {
        //   let maxBodyHeight = 0;
        //   const bodies = this.querySelectorAll('.customer-review-card__body');
        //   bodies.forEach((body) => {
        //     // Clear any inline min-height so CSS var can take effect
        //     body.style.minHeight = '';
        //     const h = body.offsetHeight;
        //     if (h > maxBodyHeight) maxBodyHeight = h;
        //   });
        //   if (maxBodyHeight > 0) {
        //     this.style.setProperty('--customer-review-body-min-height', `${maxBodyHeight}px`);
        //   }
        // });
      };

      this._updateClamp = updateClamp;

      // Initial + after layout settles.
      requestAnimationFrame(() => updateClamp());
      setTimeout(updateClamp, 250);
      window.addEventListener('resize', debounce(updateClamp, 150));

      // Click delegation to populate modal content before modal opens.
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-review-more]');
        if (!btn) return;

        const modalId = btn.getAttribute('aria-controls');
        if (!modalId) return;

        const modal = document.getElementById(modalId);
        if (!modal) return;

        const card = btn.closest('.customer-review-card');
        if (!card) return;

        const modalName = modal.querySelector('.customer-reviews-modal__name');
        const modalStars = modal.querySelector('.customer-reviews-modal__stars');
        const modalAvatar = modal.querySelector('.customer-reviews-modal__avatar');
        const modalContent = modal.querySelector('.customer-reviews-modal__content');

        const nameEl = card.querySelector('.customer-review-card__name');
        const starsEl = card.querySelector('.customer-review-card__stars');
        const avatarEl = card.querySelector('.customer-review-card__avatar, .customer-review-card__avatar--empty');
        const fullEl = card.querySelector('[data-review-full]');

        if (modalName) modalName.textContent = nameEl ? nameEl.textContent.trim() : '';
        if (modalStars) modalStars.innerHTML = starsEl ? starsEl.innerHTML : '';

        if (modalAvatar) {
          modalAvatar.innerHTML = '';
          if (avatarEl) modalAvatar.appendChild(avatarEl.cloneNode(true));
        }

        if (modalContent) modalContent.innerHTML = fullEl ? fullEl.innerHTML : '';
      });
    }

    init() {
      if (typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) {
        setTimeout(() => this.init(), 50);
        return;
      }
      this.initSlider();
    }

    initSlider() {
      if (!this.section || !this.sliderWrapper) return;

      const columnGap = FoxTheme.utils.getGridColumnGap(this) || {};
      const navNext = this.section.querySelector('.swiper-btn-next');
      const navPrev = this.section.querySelector('.swiper-btn-prev');
      const paginationEl = this.section.querySelector('.swiper-pagination');

      const sliderOptions = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: columnGap.mobile || 24,
        loop: true,
        loopAdditionalSlides: 2,
        noSwiping: true,
        noSwipingClass: 'swiper-no-swiping',
        threshold: 2,
        navigation:
          navNext && navPrev
            ? {
                nextEl: navNext,
                prevEl: navPrev,
              }
            : undefined,
        pagination:
          paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : undefined,
        breakpoints: {
          768: {
            slidesPerView: this.tabletItems,
            slidesPerGroup: 1,
            spaceBetween: columnGap.tablet || 24,
          },
          1280: {
            slidesPerView: this.items,
            slidesPerGroup: 1,
            spaceBetween: columnGap.desktop || 30,
          },
        },
      };

      if (typeof this.sliderInstance !== 'object') {
        this.classList.add('swiper');
        this.sliderWrapper.classList.remove('f-grid');
        this.sliderWrapper.classList.add('swiper-wrapper');
        this.sliderInstance = new FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        if (FoxTheme.a11y && FoxTheme.a11y.getFocusableElements) {
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((el) => {
            el.addEventListener('focusin', () => {
              // Avoid changing slides when clicking “Read more”.
              if (el.closest('[data-review-more]')) return;
              const slide = el.closest('.swiper-slide');
              if (slide && this.sliderInstance && this.sliderInstance.slider) {
                this.sliderInstance.slider.slideTo(
                  this.sliderInstance.slider.slides.indexOf(slide)
                );
              }
            });
          });
        }

        // Clamp detection depends on final layout (post-swiper init).
        if (this._updateClamp) {
          setTimeout(() => this._updateClamp(), 50);
          if (this.sliderInstance && this.sliderInstance.slider && this.sliderInstance.slider.on) {
            this.sliderInstance.slider.on('slideChange', () => this._updateClamp());
            this.sliderInstance.slider.on('resize', () => this._updateClamp());
          }
        }
      }

      if (typeof Shopify !== 'undefined' && Shopify.designMode && this.sliderInstance && this.sliderInstance.slider) {
        document.addEventListener('shopify:block:select', (e) => {
          if (!e.detail || e.detail.sectionId !== this.sectionId) return;
          const index = Number(e.target.dataset.index);
          if (!isNaN(index)) this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove('swiper');
      if (this.sliderWrapper) {
        this.sliderWrapper.classList.remove('swiper-wrapper');
        this.sliderWrapper.classList.add('f-grid');
      }
      if (this.sliderInstance && this.sliderInstance.slider) {
        this.sliderInstance.slider.destroy();
      }
      this.sliderInstance = false;
    }
  }
  customElements.define('customer-reviews-component', CustomerReviews);
}
